import { useState } from 'react';
import { INSPECTION_SLOTS } from '@adbonus/core';
import { useI18n } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { photoUrl } from '../../lib/photo';
import type { Inspection, InspectionPhoto } from '../../lib/types';
import { PhotoViewer } from './PhotoViewer';
import { usePhotoLabel } from './labels';

/** Sides first in the order they are walked, extras after, damage last in the order it was found. */
export function orderPhotos(photos: InspectionPhoto[]): InspectionPhoto[] {
  const rank = (p: InspectionPhoto) => INSPECTION_SLOTS.indexOf(p.slot);
  return [...photos].sort((a, b) => rank(a) - rank(b) || a.createdAt.localeCompare(b.createdAt));
}

/** A finished walk-round, read-only: who took it, when, and every photo. */
export function InspectionGallery({ inspection }: { inspection: Inspection }) {
  const { t } = useI18n();
  const label = usePhotoLabel();
  const photos = orderPhotos(inspection.photos);
  const damages = photos.filter((p) => p.slot === 'DAMAGE');
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-ink">
          {inspection.kind === 'INTAKE' ? t('inspection.intakeTitle') : t('inspection.handoverTitle')}
        </span>
        <span className="tabular text-[0.7rem] text-ink-faint">
          {formatDateTime(inspection.completedAt ?? inspection.createdAt)}
          {inspection.createdByName && ` · ${t('inspection.by', { name: inspection.createdByName })}`}
        </span>
      </div>
      <p className="mt-0.5 text-[0.7rem] text-ink-dim">
        {t('inspection.photos', { count: photos.length })}
        {damages.length > 0 && <span className="text-warning"> · {t('inspection.damageCount', { count: damages.length })}</span>}
        {inspection.odometerKm !== null && ` · ${inspection.odometerKm.toLocaleString('ru-RU')} км`}
      </p>

      <ul className="mt-2.5 grid grid-cols-3 gap-2 sm:grid-cols-4">
        {photos.map((photo, index) => (
          <li key={photo.id}>
            <button
              onClick={() => setOpen(index)}
              className="group relative block aspect-[4/3] w-full overflow-hidden rounded-xl border border-line bg-raised"
            >
              <img
                src={photoUrl(photo.id, 'thumb')}
                alt={label(photo)}
                loading="lazy"
                className="h-full w-full object-cover transition group-hover:scale-[1.03]"
              />
              <span className={`absolute inset-x-0 bottom-0 truncate px-1.5 py-1 text-left text-[0.62rem] font-medium text-white ${
                photo.slot === 'DAMAGE' ? 'bg-warning/85' : 'bg-black/55'
              }`}>
                {label(photo)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {open !== null && (
        <PhotoViewer photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}
